import { useEffect } from 'react';

/**
 * Lenis is a progressive enhancement for precise-pointer desktop/laptop input.
 *
 * WASHED deliberately does not snap between full-screen sections. The premium
 * feel should come from stable typography, natural document rhythm and subtle
 * inertial scrolling—not from forcing the reader into a slideshow.
 */
export function useSmoothScroll() {
  useEffect(() => {
    const mq = window.matchMedia;
    if (!mq) return;
    if (mq('(prefers-reduced-motion: reduce)').matches) return;
    if (!mq('(pointer: fine)').matches) return;

    let cancelled = false;
    let rafId = 0;
    let destroy: (() => void) | null = null;

    void (async () => {
      const { default: Lenis } = await import('lenis');
      if (cancelled) return;

      // lerp 0.2 settles within a few frames; 0.09 visibly trailed the wheel,
      // which reads as input lag rather than polish. Wheel distance stays 1:1.
      const lenis = new Lenis({
        lerp: 0.2,
        smoothWheel: true,
        wheelMultiplier: 1,
      });

      const raf = (time: number) => {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);

      const onClick = (event: MouseEvent) => {
        const link = (event.target as HTMLElement | null)?.closest?.(
          'a[href^="#"]',
        ) as HTMLAnchorElement | null;
        const hash = link?.getAttribute('href');
        if (!hash || hash === '#') return;
        const target = document.querySelector(hash);
        if (!target) return;

        event.preventDefault();
        lenis.scrollTo(target as HTMLElement, { offset: -96 });
        // preventDefault also cancels the browser's focus move, so do it here:
        // keyboard and screen-reader users must land where the page scrolled.
        const el = target as HTMLElement;
        if (!el.matches('a[href], button, input, select, textarea, [tabindex]')) {
          el.setAttribute('tabindex', '-1');
        }
        el.focus({ preventScroll: true });
      };

      document.addEventListener('click', onClick);

      destroy = () => {
        document.removeEventListener('click', onClick);
        lenis.destroy();
      };
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      destroy?.();
    };
  }, []);
}
